import { Switch as BaseSwitch } from '@base-ui/react/switch';
import type { ComponentProps } from 'react';

import { cn } from '@core/utils/cn';

export interface SwitchProps extends ComponentProps<typeof BaseSwitch.Root> {}

/**
 * A toggle.
 *
 * <p>State arrives as `data-checked` rather than through the native `:checked` pseudo-class
 * — Base UI's root is a button, not a checkbox. Styling it with `:checked` silently does
 * nothing, which is the single easiest mistake to make with this library.
 */
export function Switch({ className, ...props }: SwitchProps) {
  return (
    <BaseSwitch.Root
      className={cn(
        'rounded-pill bg-fill-subtle relative h-6 w-10 shrink-0 p-0.5',
        'ring-separator ring-1 transition-colors duration-200 ring-inset',
        'data-[checked]:bg-accent data-[checked]:ring-accent',
        'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <BaseSwitch.Thumb
        className={cn(
          'bg-surface shadow-e1 block size-5 rounded-full',
          'ease-spring transition-transform duration-200 motion-reduce:transition-none',
          'data-[checked]:translate-x-4',
        )}
      />
    </BaseSwitch.Root>
  );
}
